import { Button } from '@heidi/design-system/button';
import { TypographyCaption, UserData } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';

import type { SuggestedPatientMatch } from '../suggested-matches';

interface SuggestedMatchesSectionProps {
  matches: readonly SuggestedPatientMatch[];
  /**
   * Links/selects the tapped patient directly — no review step. Each row is
   * name-only (matching the "All patients" rows); secondary info
   * (gender/DOB/medical history) is intentionally stripped.
   */
  onSelectMatch: (match: SuggestedPatientMatch) => void;
  className?: string;
}

/**
 * Inline, name-only list of suggested patient matches. Tapping a name links the
 * patient directly. Replaces the former "{count} Patient match(es) found"
 * review CTA. Styled to match the "All patients" rows.
 */
export const SuggestedMatchesSection: React.FC<SuggestedMatchesSectionProps> = ({
  matches,
  onSelectMatch,
  className,
}) => {
  if (matches.length === 0) return null;

  return (
    <div className={cn('flex flex-col', className)}>
      {matches.map((match) => (
        <Button
          key={match.id}
          type="button"
          variant="ghost"
          size="sm"
          disableAutomaticSvgSizing
          // Keep focus on the search input so the dropdown stays open while the
          // click resolves (mousedown moves focus before click fires).
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onSelectMatch(match);
          }}
          className="flex w-full justify-start gap-2 px-2"
        >
          <TypographyCaption className="truncate">
            <UserData>{match.fullName}</UserData>
          </TypographyCaption>
        </Button>
      ))}
    </div>
  );
};
